import Manager from 'coreClasses/Manager';
import {
	cleanupOrders,
	receiveLoadingStatus,
	receiveOrder,
	receiveOrders,
	selectLoadingStatus,
	selectOrder,
	selectOrders,
	selectPages,
	selectPagination,
} from 'coreStore/data/orders';
import OrderModel from './OrderModel';
import {MY_ACCOUNT_ORDERS_PER_PAGE} from 'brand/app/constants';
import {receiveHasOrders} from 'coreStore/data/customer';
import PWAException from 'coreClasses/PWAException';
import {EEventType} from 'coreClasses/events/IEvents';
import ManagerRepository from '../ManagerRepository';

import {Order, OrdersPages, OrdersPagination} from '../../store/data/orders/types';

const FAILED_ORDER_STATUS = 'failed';

/**
 * Represents OrdersMgr
 */
export class OrdersMgrInstance extends Manager {
	private key: string;
	private offset: number;

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 * @param {String} key - Key to pass to get the proper instance
	 */
	constructor(managerRepository, key) {
		super(managerRepository);

		this.key = key;
		//TODO: Change the logic Of MY_ACCOUNT_ORDERS_PER_PAGE because it could be different from the one sent to the call
		this.offset = -MY_ACCOUNT_ORDERS_PER_PAGE;
	}

	/**
	 * Retrieve all customer orders by combining results from multiple pages.
	 * @returns {Array<OrderModel>} Array of customer orders.
	 */
	getCustomerOrders(): Array<OrderModel> {
		return this.getStoredOrders()
			.filter((o) => o.status !== FAILED_ORDER_STATUS)
			.map((o) => new OrderModel(this.managerRepository, o));
	}

	/**
	 * Retrieve customer orders grouped by pages, excluding orders with a failed status.
	 * @returns {Array<OrderModel>} Array of customer orders grouped by pages.
	 */
	getCustomerOrdersByPages(): Array<OrderModel[]> {
		const pages = Object.values(this.getStoredPages()) as unknown as Order[][];

		return pages.map((pageOrders) =>
			pageOrders.filter((o) => o.status !== FAILED_ORDER_STATUS).map((o) => new OrderModel(this.managerRepository, o)),
		);
	}

	/**
	 * Check if the last page of customer orders has been loaded.
	 * @returns {boolean} Returns true if the last page has been loaded, otherwise false.
	 */
	isLastPageLoaded(): boolean {
		const pagination = this.getStoredPagination();

		if (!pagination.total === null) {
			return false;
		} else {
			const storedOrdersTotal = pagination.total;
			const storedOrdersShowed = pagination.limit + pagination.offset;

			return storedOrdersShowed === storedOrdersTotal;
		}
	}

	/**
	 * Retrieve a guest order by order number and zip code
	 * @param {String} orderNo order number
	 * @param {String} zipCode billing zip code
	 * @returns {Promise<OrderModel>} order model
	 */
	async getOrderByZipCode(orderNo: string, zipCode: string): Promise<OrderModel> {
		const response = await this.managerRepository.getCommerceApi().customShopperOrders.getOrderByZipCode({
			pathParameters: {
				c_orderNo: orderNo,
			},
			parameters: {
				c_zipCode: zipCode
			}
		}, true) as {order: Order; error?: boolean; errorMessage?: string};

		if (response.error) {
			throw new PWAException(null, response.errorMessage);
		}

		return new OrderModel(this.managerRepository, response.order);
	}

	/**
	 * Retrieve a customer order.
	 * @returns {Promise<OrderModel>} order model
	 */
	async getCustomerOrder(orderNumber: string): Promise<OrderModel> {
		let order = this.getStoredOrder(orderNumber);

		if (order) {
			return new OrderModel(this.managerRepository, order);
		} else {
			const order = await this.managerRepository.getCommerceApi().shopperOrders.getOrder({
				parameters: {orderNo: orderNumber},
			});

			this.storeOrder(order);

			return new OrderModel(this.managerRepository, order);
		}
	}

	/**
	 * Retrieve the last customer order
	 * @returns {OrderModel | null} last customer order by creation date
	 */
	getLastOrder(): OrderModel | null {
		let orders = this.getCustomerOrders();

		return orders.length ? orders[0] : null;
	}

	/**
	 * Retrieve a page of customer orders.
	 * @param {object} params - Additional parameters for the API call.
	 * @param {number} page - Page requested.
	 * @returns {Promise<void>} - A promise that resolves when the page of orders is fetched and stored.
	 */
	async fetchPage(params: any, page: number = 0) {
		//TODO: Change the logic Of MY_ACCOUNT_ORDERS_PER_PAGE because it could be different from the one sent to the call
		const limit = MY_ACCOUNT_ORDERS_PER_PAGE;
		const newOffset = page * limit;

		if (this.isOrdersLoading()) {
			return;
		}

		this.setOrdersLoading(true);
		const orders = await this.managerRepository.getCommerceApi().shopperCustomers.getCustomerOrders({
			parameters: {
				customerId: this.managerRepository.getCustomerMgr().getCurrentCustomer().customerId,
				offset: newOffset,
				limit: MY_ACCOUNT_ORDERS_PER_PAGE,
				...params,
			},
		});

		this.storeOrders(orders);
	}

	/**
	 * Retrieve the next page of customer orders.
	 * @param {object} params - Additional parameters for the API call.
	 * @returns {Promise<void>} - A promise that resolves when the next page of orders is fetched and stored.
	 */
	async fetchNextPage(params: any) {
		if (this.isLastPageLoaded()) {
			return;
		}

		const limit = MY_ACCOUNT_ORDERS_PER_PAGE;
		const currentPage = (this.offset + limit) / limit;
		const newOffset = currentPage * limit;

		await this.fetchPage(params, currentPage);

		this.offset = newOffset;
	}

	/**
	 * Checks if orders are loading (api call on fly)
	 * @returns {boolean} true if orders are loading, false otherwise
	 */
	isOrdersLoading() {
		const store = this.managerRepository.getStoreMgr().getStore();
		const loadingStatus = selectLoadingStatus(store.getState(), this.key);

		return loadingStatus.isLoading;
	}

	/**
	 * Get count of all orders from store
	 * @returns total amount of orders without failed orders
	 */
	getTotalOrders(): number {
		const pagination = this.getStoredPagination();
		const orders = this.getStoredOrders();
		const failedOrders = orders.filter((o) => o.status === FAILED_ORDER_STATUS);

		if (failedOrders.length) {
			return pagination.total - failedOrders.length;
		}

		return pagination.total;
	}

	/**
	 * Gets orders that are already fetched and stored in redux
	 * @returns Orders[]
	 */
	private getStoredOrders(): Order[] {
		const store = this.managerRepository.getStoreMgr().getStore();

		return Object.values(selectOrders(store.getState(), this.key));
	}

	/**
	 * Gets orders pages that are already fetched and stored in redux
	 * @returns OrdersPage[]
	 */
	private getStoredPages(): OrdersPages {
		const store = this.managerRepository.getStoreMgr().getStore();

		return selectPages(store.getState(), this.key);
	}

	/**
	 * Gets the pagination informations for the current instance
	 * @returns OrdersPagination
	 */
	private getStoredPagination(): OrdersPagination {
		const store = this.managerRepository.getStoreMgr().getStore();

		return selectPagination(store.getState(), this.key);
	}

	/**
	 * Gets an order by orderNo
	 * @returns Order
	 */
	private getStoredOrder(orderNo: string): Order {
		const store = this.managerRepository.getStoreMgr().getStore();

		return selectOrder(store.getState(), this.key, orderNo);
	}

	/**
	 * Set orders loading
	 * @param {boolean} isLoading value to assign to isLoading
	 */
	private setOrdersLoading(isLoading: boolean) {
		const store = this.managerRepository.getStoreMgr().getStore();

		(store.dispatch as (action: {type: string; payload?: unknown}) => void)(
			receiveLoadingStatus({isLoading, key: this.key}),
		);
	}

	/**
	 * Adds orders to redux store
	 * @param apiOrders {Object} - Order data from API
	 */
	private storeOrders(apiOrders) {
		const store = this.managerRepository.getStoreMgr().getStore();

		store.dispatch(
			receiveOrders({
				...apiOrders,
				key: this.key,
			}),
		);

		if (apiOrders.total) {
			store.dispatch(receiveHasOrders(!!apiOrders.total));

			this.managerRepository.getEventsMgr().emit(EEventType.CUSTOMER_CHANGED, {
				customer: this.managerRepository.getCustomerMgr().getCurrentCustomer(),
			});
		}

		this.setOrdersLoading(false);
	}

	/**
	 * Adds order to redux store
	 * @param apiOrder {Object} - Order data from API
	 */
	private storeOrder(apiOrder) {
		const store = this.managerRepository.getStoreMgr().getStore();

		(store.dispatch as (action: {type: string; payload?: unknown}) => void)(
			receiveOrder({order: apiOrder, key: this.key}),
		);
	}
}

export const DEFAULT_KEY = 'default';

/**
 * Represents OrdersMgrMap
 */
class OrdersMgrMap extends Manager {
	ordersMgrMap: Map<string, OrdersMgrInstance>;
	static;

	/**
	 * @constructor
	 * @param managerRepository {ManagerRepository} - ManagerRepository instance
	 */
	constructor(managerRepository: ManagerRepository) {
		super(managerRepository);

		this.ordersMgrMap = new Map();
	}

	/**
	 * Removes all stored orders from redux store
	 */
	cleanupOrders() {
		const store = this.managerRepository.getStoreMgr().getStore();

		store.dispatch(cleanupOrders());
		store.dispatch(receiveHasOrders());
		this.ordersMgrMap = new Map();
	}

	/**
	 * Returns the correct instance of OrdersMgr for the given key
	 * @param key {String} - the instance key
	 */
	getInstance(key: string = DEFAULT_KEY): OrdersMgrInstance {
		if (!this.ordersMgrMap.has(key)) {
			this.ordersMgrMap.set(key, new OrdersMgrInstance(this.managerRepository, key));
		}

		return this.ordersMgrMap.get(key);
	}

	/**
	 * Retrieves the return link for a given order
	 * @param {String} orderNo order number
	 * @returns {string} return link
	 */
	async getReturnLink(orderNo: string): Promise<string> {
		const response = await this.managerRepository.getCommerceApi().customShopperReturnAuthorizations.getReturnLink(
			{
				body: {
					c_orderNo: orderNo,
				},
			},
			false,
		);

		return response.returnLink;
	}
}

export default OrdersMgrMap;
